import { useEffect, useState } from 'react';
import api from '../../../services/api';
import { useCapacityPreview } from './model';
import { Select } from './CapacityForms';
interface Day { date: string; required: number; available: number; projected: number }
interface Counter { pools: Record<string, Day[]>; combined: boolean; deliveryRate: number; prepullRate: number; undated: number }
export default function CapacityCounter({ market }: { market: string }) {
  const { requests } = useCapacityPreview();
  const [data, setData] = useState<Counter | null>(null);
  const [pool, setPool] = useState('Local');
  const [error, setError] = useState('');
  // Blank only when the market changes, where the old numbers would be wrong.
  // A refetch triggered by the queue changing keeps the current figures on
  // screen until the new ones land, so the table does not flash empty.
  useEffect(() => { setData(null); setError(''); }, [market]);
  useEffect(() => {
    let active = true;
    api.get<Counter>(`/capacity-requests/counter/${encodeURIComponent(market)}`).then(result => {
      if (active) { setData(result.data); setError(''); }
    }).catch(() => { if (active) setError('Capacity counts could not be loaded.'); });
    return () => { active = false; };
  }, [market, requests]);
  const days = data?.pools[data.combined ? 'Combined' : pool] ?? [];
  return <section className="rounded-lg border border-theme-border-primary bg-panel p-4">
    <h3 className="font-semibold text-heading">{market} capacity</h3>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {!data && !error && <p className="text-sm text-muted">Loading capacity…</p>}
    {data && <>
      {data.combined ? <p className="text-sm text-muted">Combined truck pool</p> : <Select label="Capacity pool" value={pool} options={['Local', 'OTR']} onChange={setPool} />}
      <div className="overflow-auto"><table className="mt-3 w-full text-sm"><thead><tr><th className="p-2 text-left">Trucks</th>{days.map(d => <th key={d.date}>{d.date.slice(5)}</th>)}</tr></thead><tbody>{([['required', 'Required'], ['available', 'Available'], ['projected', 'If all approved']] as const).map(([key, label]) => <tr key={key} className="border-t border-theme-border-primary"><th className="p-2 text-left">{label}</th>{days.map(d => <td className="p-2 text-center" key={d.date}>{d[key]}</td>)}</tr>)}</tbody></table></div>
      <p className="mt-2 text-xs text-muted">Estimate: {data.deliveryRate} local deliveries and {data.prepullRate} prepulls per truck/day. OTR uses one truck per move. Adjust in Terminal Settings.</p>
      {data.undated > 0 && <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{data.undated} ASAP request(s) need dates before they can be included.</p>}
    </>}
  </section>;
}
